import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, CreditCard, ExternalLink, CheckCircle2, AlertTriangle, XCircle,
  CalendarClock, RefreshCw, Receipt, ArrowUpCircle, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import api, { getErrorMessage } from "@/lib/api";

const PLAN_LABEL = { free: "Free", pro: "Pro Host", pm: "Property Manager" };
const PLAN_PRICE = { free: "$0", pro: "$19", pm: "$99" };

const INTERVAL_LABEL = { monthly: "Monthly", "6mo": "6 Months", "12mo": "12 Months" };

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";

const priceText = (sub) => {
  if (sub?.billing_amount != null && sub?.billing_interval_suffix) {
    const suffix = sub.billing_interval_suffix; // "/month" | "every 6 months" | "every 12 months"
    const sep = suffix.startsWith("/") ? "" : " ";
    return `$${sub.billing_amount}${sep}${suffix} AUD`;
  }
  return `${PLAN_PRICE[sub?.plan || "free"]}/month AUD`;
};

const StatusPill = ({ status, canceling }) => {
  const map = {
    active: { c: "bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]", I: CheckCircle2, t: "Active" },
    past_due: { c: "bg-[hsl(var(--risk-high-bg))] text-[hsl(var(--risk-high))]", I: AlertTriangle, t: "Payment failed" },
    canceled: { c: "bg-muted text-muted-foreground", I: XCircle, t: "Canceled" },
    none: { c: "bg-muted text-muted-foreground", I: CheckCircle2, t: "Free plan" },
  };
  const cfg = map[status] || map.none;
  const I = cfg.I;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {/* Status stays Active until the period end — paid access continues */}
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${cfg.c}`} data-testid="billing-status-pill">
        <I className="h-3 w-3" /> {cfg.t}
      </span>
      {canceling && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--risk-medium-bg))] px-2.5 py-1 text-xs font-medium text-[hsl(var(--risk-medium))]" data-testid="billing-cancel-badge">
          <AlertTriangle className="h-3 w-3" /> Cancels at period end
        </span>
      )}
    </span>
  );
};

export default function Billing() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [acting, setActing] = useState(false);

  const loadSub = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/subscription");
      setSub(r.data);
    } catch (e) {
      console.error("Failed to load subscription", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSub(); }, [loadSub]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/subscription/portal");
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Could not open billing portal.");
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not open billing portal."));
    } finally {
      setPortalLoading(false);
    }
  };

  const cancelSub = async () => {
    setActing(true);
    try {
      const r = await api.post("/subscription/cancel");
      toast.success(`Subscription will end on ${fmtDate(r.data.access_until)}. You keep access until then.`);
      await Promise.all([loadSub(), refreshUser()]);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not cancel."));
    } finally { setActing(false); }
  };

  const resumeSub = async () => {
    setActing(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Subscription resumed.");
      await Promise.all([loadSub(), refreshUser()]);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not resume."));
    } finally { setActing(false); }
  };

  const planId = sub?.plan || "free";
  const isPaid = planId !== "free";

  return (
    <AppShell title="Billing" action={
      <button onClick={loadSub} className="text-muted-foreground hover:text-foreground" aria-label="Refresh" data-testid="billing-refresh">
        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
      </button>
    }>
      <div className="max-w-3xl space-y-6" data-testid="billing-page">
        {loading ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : sub?.owner_complimentary ? (
          <div
            className="rounded-xl border border-primary/25 bg-primary/5 p-6"
            data-testid="billing-owner-complimentary"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-base font-semibold text-foreground">
                  Owner account — complimentary access
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Your owner account has permanent, unrestricted access to every current and future
                  feature (Property Manager tier and beyond). Billing is disabled for this account —
                  you will never be enrolled in or charged for a subscription.
                </p>
                <p className="mt-3 text-sm text-muted-foreground">
                  Want to see how paid tiers look to customers? Use the{" "}
                  <span className="font-medium text-foreground">Owner Test Mode</span> control
                  (bottom-left) to simulate Free, Pro Host or Property Manager without affecting your
                  billing or permissions.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Current plan */}
            <div className="rounded-xl border bg-card p-6" data-testid="billing-current-plan">
              <div className="mb-5 flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-primary" />
                <h3 className="font-display text-base font-semibold">Your subscription</h3>
              </div>

              <div className="flex flex-col gap-3 rounded-lg border bg-secondary/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-display text-xl font-semibold" data-testid="billing-plan-name">{PLAN_LABEL[planId]}</p>
                    <StatusPill status={sub?.status} canceling={sub?.cancel_at_period_end} />
                    {isPaid && sub?.billing_interval && (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
                        data-testid="billing-interval-badge"
                      >
                        {sub.billing_interval_label || INTERVAL_LABEL[sub.billing_interval] || "Monthly"}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground" data-testid="billing-price-text">
                    {isPaid ? priceText(sub) : "10 assessments per month · no AI summary"}
                  </p>
                </div>
                {!isPaid && (
                  <Button onClick={() => navigate("/app/pricing")} className="gap-2" data-testid="billing-upgrade-button">
                    <ArrowUpCircle className="h-4 w-4" /> Upgrade plan
                  </Button>
                )}
              </div>

              {isPaid && (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border p-3">
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" /> {sub?.cancel_at_period_end ? "Active until" : "Renews on"}</p>
                    <p className="mt-1 text-sm font-medium" data-testid="billing-renewal-date">{fmtDate(sub?.current_period_end)}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><RefreshCw className="h-3.5 w-3.5" /> Billing cycle</p>
                    <p className="mt-1 text-sm font-medium" data-testid="billing-cycle-value">{sub?.billing_interval_label || INTERVAL_LABEL[sub?.billing_interval] || "Monthly"}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Receipt className="h-3.5 w-3.5" /> Last payment</p>
                    <p className="mt-1 text-sm font-medium">{sub?.last_payment ? `$${sub.last_payment.amount} ${String(sub.last_payment.currency).toUpperCase()}` : "—"}</p>
                  </div>
                </div>
              )}

              {sub?.status === "past_due" && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-[hsl(var(--risk-high))]/25 bg-[hsl(var(--risk-high-bg))] p-3 text-sm text-[hsl(var(--risk-high))]">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Your last payment failed. Please update your payment method in the billing portal to keep your subscription active.</span>
                </div>
              )}
            </div>

            {/* Manage billing (Stripe portal) */}
            <div className="rounded-xl border bg-card p-6" data-testid="billing-portal-card">
              <h3 className="font-display text-base font-semibold">Manage billing</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Open the secure Stripe customer portal to update payment methods, view & download invoices, and manage your subscription.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={openPortal}
                  disabled={portalLoading || !sub?.has_stripe_customer}
                  className="gap-2"
                  data-testid="billing-open-portal-button"
                >
                  {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ExternalLink className="h-4 w-4" /> Open billing portal</>}
                </Button>

                {isPaid && (
                  sub?.cancel_at_period_end ? (
                    <Button variant="outline" onClick={resumeSub} disabled={acting} data-testid="billing-resume-button">
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resume subscription"}
                    </Button>
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="text-muted-foreground" data-testid="billing-cancel-button">Cancel subscription</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                          <AlertDialogDescription>
                            You'll keep {PLAN_LABEL[planId]} access until {fmtDate(sub?.current_period_end)}, after which your account reverts to the Free plan. You won't be charged again.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="billing-cancel-keep">Keep subscription</AlertDialogCancel>
                          <AlertDialogAction onClick={cancelSub} data-testid="billing-cancel-confirm">Cancel subscription</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )
                )}
              </div>
              {!sub?.has_stripe_customer && (
                <p className="mt-3 text-xs text-muted-foreground">The billing portal becomes available once you start a paid subscription.</p>
              )}
              <p className="mt-4 text-xs text-muted-foreground">Payments are processed securely by Stripe. Prices in AUD.</p>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
